import { Autocomplete, Box, Button, Checkbox, FormControl, FormControlLabel, FormGroup, Grid, InputLabel, Link, MenuItem, Paper, Select, Stack, styled, SvgIcon, TextField, useMediaQuery, useTheme } from '@mui/material';
import MessagesSnackbar from '../../components/shared/MessagesSnackbar';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import useLogError from '../../hooks/useLogError';
import { SyntheticEvent, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddODSModel, client, DeliveryScheduleFilterOptions, Instance, Market, MarketOrderType, MarketOrderTypeFilterOptions, MarketStoreFilterOptions, MarketSupplier, ODSImportResultModel, OrderType, Supplier } from '../../app/services/api/orderManagementClient';
import { PaginatedList } from '../../app/services/api/orderManagementClient';
import { isAutoShipOrder } from '../../enums/OrderType';
import { DeliveryScheduleModel } from '../../app/services/api/orderManagementClient';
import DeliveryScheduleTable from './components/DeliveryScheduleTable';
import { SearchSm, Trash01 } from '../../assets';
import { Store } from '../../app/services/api/generated';
import DatePickerLocalized from '../../components/shared/DatePickerLocalized';
import { base64ToBlob, filterStoreOptions } from '../../utils';
import DownloadIcon from '@mui/icons-material/Download';
import { Upload as UploadIcon } from '@mui/icons-material';
import ODSUploadModal from './modals/ODSUploadModal';
import DateSelectionModal from './modals/DateSelectionModal';
import { ConfirmationDialog } from '../../components/shared/dialogs/ConfirmationDialog';
import AddODSModal from './modals/AddODSModal';
import AddIcon from '@mui/icons-material/AddCircle';
import LoadingWheel from '../../components/ui/LoadingWheel';

const ManageDeliverySchedule = () => {

  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation('odsmanagement');
  const { hasPermissionTo, isAdmin, selectedStore } = useContext(UserContext);
  const hasWritePermissions = hasPermissionTo(['ODSWrite']) || isAdmin();
  const hasDeletePermissions = hasPermissionTo(['ODSDelete']);
  const { logError } = useLogError();

  const [instances, setInstances] = useState<Instance[]>([]);
  const [instanceId, setInstanceId] = useState<number>(0);

  const [markets, setMarkets] = useState<Market[]>([]);
  const [marketId, setMarketId] = useState<number>(0);

  const pendingMarketId = useRef<number | null>(null);
  const pendingAutoSearch = useRef<boolean>(false);

  const [orderTypes, setOrderTypes] = useState<MarketOrderType[]>([]);
  const [selectedOrderTypes, setSelectedOrderTypes] = useState<MarketOrderType[]>([]);
  const [checkedAllOrderTypes, setCheckedAllOrderTypes] = useState<boolean>(false);

  const [suppliers, setSuppliers] = useState<MarketSupplier[]>([]);
  const [supplier, setSupplier] = useState<string>('');

  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [checkedAllStores, setCheckedAllStores] = useState<boolean>(false);

  const [deliveryDateFrom, setDeliveryDateFrom] = useState('');
  const [deliveryDateTo, setDeliveryDateTo] = useState('');

  const [cutOffDateFrom, setCutOffDateFrom] = useState('');
  const [cutOffDateTo, setCutOffDateTo] = useState('');

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');
  const [paginatedData, setPaginatedData] = useState<PaginatedList<DeliveryScheduleModel>>();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('id');
  const defaultSort = 'id';
  const defaultDirection = 'desc';
  const defaultPageSize = 10;

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [currentFileRequest, setCurrentFileRequest] = useState<{ fileData: string; fileName: string } | null>(null);

  const [selectedItems, setSelectedItems] = useState<number[]>([]);

  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [dateModalType, setDateModalType] = useState<'cutoff' | 'delivery'>('cutoff');
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);

  const [createODSModalOpen, setCreateODSModalOpen] = useState(false);
  const [isSelectAll, setIsSelectAll] = useState(false);

  useEffect(() => {
    loadInstances();
    loadDefaultFilters();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (instanceId > 0 && marketId > 0) {
        setIsLoading(true);
        await loadSuppliers(instanceId, marketId);
        await loadOrderTypes();
        await loadStores();
        if (pendingAutoSearch.current) {
          pendingAutoSearch.current = false;
          await loadFilteredData(getFilters());
        }
      } else {
        setSuppliers([]);
        setSupplier('');
        setSelectedOrderTypes([]);
        setOrderTypes([]);
        setStores([]);
        setSelectedStores([]);
        setPage(0);
        setPaginatedData(undefined);
      }
    };

    fetchData();
  }, [marketId]);

  useEffect(() => {
    if (instanceId && marketId && paginatedData) {
      loadFilteredData(getFilters(), false);
    }
  }, [page, order, orderBy]);

  useEffect(() => {
    if (instanceId) {
      loadMarkets(instanceId);
    } else {
      setMarkets([]);
      setMarketId(0);
    }
  }, [instanceId]);

  const loadFilteredData = async (options: DeliveryScheduleFilterOptions, clearSelections = true) => {
    setIsLoading(true);

    if (clearSelections) {
      setSelectedItems([]);
      setIsSelectAll(false);
    }

    try {
      const result = await client.getODS(options, selectedStore?.storeNumber ?? '');

      if (result.success) {
        setPaginatedData(result.data);
      } else {
        logError(result.message);
      }
    } catch (error) {
      logError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSnackbarOpen = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const loadDefaultFilters = async () => {
    if (!selectedStore?.storeNumber || !selectedStore?.franchiseUUID) return;
    try {
      const companyResult = await client.getCompanyIdByStoreNumber(selectedStore.storeNumber);
      if (!companyResult.success || !companyResult.data) return;

      const newInstanceId = companyResult.data;
      const marketsResponse = await client.getMarkets({
        companyId: newInstanceId.toString(),
        pageNumber: 0,
        pageSize: 2147483647,
      });

      if (marketsResponse.success && marketsResponse.data?.items) {
        const storeMarket = marketsResponse.data.items.find(m => m.uuid === selectedStore!.franchiseUUID);
        if (storeMarket) {
          pendingMarketId.current = storeMarket.customerTypeId;
          pendingAutoSearch.current = true;
          setInstanceId(newInstanceId);
        }
      }
    } catch (error) {
      logError('Failed to load default filters');
    }
  };

  const loadInstances = async () => {
    try {
      const response = await client.getInstances({
        pageNumber: 0,
        pageSize: 2147483647,
      });

      if (response.success && response.data) {
        setInstances(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load instances');
    }
  };

  const loadMarkets = async (instanceId: number) => {
    try {
      const response = await client.getMarkets({
        companyId: instanceId?.toString(),
        pageNumber: 0,
        pageSize: 2147483647,
      });

      if (response.success && response.data) {
        setMarkets(response.data.items || []);
        if (pendingMarketId.current !== null) {
          setMarketId(pendingMarketId.current);
          pendingMarketId.current = null;
        } else {
          setMarketId(0);
        }
      }
    } catch (error) {
      logError('Failed to load markets');
    }
  };

  const loadOrderTypes = async () => {
    try {
      const options: MarketOrderTypeFilterOptions = {
        companyId: instanceId?.toString() || undefined,
        customerTypeId: marketId?.toString() || undefined,
        searchTerm: undefined,
        pageNumber: 0,
        pageSize: 999999,
        sort: '',
        direction: 'asc',
      };
      const result = await client.getMarketOrderTypes(options);

      if (result.success && result.data) {
        const nonAutoshipOrderTypes = result.data.items.filter(x => !isAutoShipOrder(x.orderTypeId));
        setOrderTypes(nonAutoshipOrderTypes);
      } else {
        setOrderTypes([]);
      }
    } catch (error) {
      console.error('Error fetching order types:', error);
      setOrderTypes([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSuppliers = async (companyId: number, customerTypeId?: number) => {
    try {
      const result = await client.getMarketSuppliers({
        companyId: companyId.toString(),
        customerTypeId: customerTypeId?.toString(),
        pageNumber: 0,
        pageSize: 2147483647,
      });
      if (result.success && result.data) {
        setSuppliers(result.data.items);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const loadStores = async () => {
    try {
      const filterOptions: MarketStoreFilterOptions = {
        companyId: instanceId?.toString(),
        customerTypeId: marketId?.toString(),
        searchTerm: undefined,
        pageNumber: 0,
        pageSize: 2147483647,
      };

      const response = await client.getMarketStores(filterOptions);

      if (response.success && response.data) {
        setStores(response.data.items || []);
      } else {
        logError('Failed to load stores');
      }
    } catch (error) {
      logError('Failed to load stores');
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSelectAllStoresChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSelectedStores(checked ? stores : []);
    setCheckedAllStores(checked);
  };

  const handleSelectAllOrderTypesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSelectedOrderTypes(checked ? orderTypes : []);
    setCheckedAllOrderTypes(checked);
  };

  const searchClick = () => {
    loadFilteredData(getFilters());
    setPage(0);
  };

  const handleSelectAllChange = async (isSelectAll: boolean) => {
    setIsSelectAll(isSelectAll);

    if (isSelectAll) {
      try {
        const allItemsFilters = {
          ...getFilters(),
          pageNumber: 0,
          pageSize: 999999,
        };

        const result = await client.getODS(allItemsFilters, selectedStore?.storeNumber ?? '');

        if (result.success && result.data?.items) {
          const allItemIds = result.data.items
            .filter(item => item.id !== undefined && item.id !== null)
            ?.map(item => item.id!);

          setSelectedItems(allItemIds);
        } else {
          logError(result.message || 'Failed to fetch all items');
          setIsSelectAll(false);
        }
      } catch (error) {
        logError('Error fetching all items for selection');
        setIsSelectAll(false);
      } finally {
        setIsLoading(false);
      }
    } else {
      setSelectedItems([]);
    }
  };

  const handleItemSelectionChange = (itemId: number, isChecked: boolean) => {
    if (isChecked) {
      if (!selectedItems.some(e => e === itemId)) {
        setSelectedItems(prev => [...prev, itemId]);
      }
    } else {
      if (isSelectAll) {
        setIsSelectAll(false);
      }
      setSelectedItems(prev => prev.filter(i => i !== itemId));
    }
  };

  const getFilters = (): DeliveryScheduleFilterOptions => {
    const orderTypesToSend = selectedOrderTypes.length > 0 ? selectedOrderTypes : [];

    return {
      instanceId: instanceId,
      marketId: marketId,
      orderTypes: orderTypesToSend.map(x => x.orderTypeId).join(','),
      supplierNumber: supplier,
      deliveryDateFrom: deliveryDateFrom,
      deliveryDateTo: deliveryDateTo,
      cutOffDateFrom: cutOffDateFrom,
      cutOffDateTo: cutOffDateTo,
      stores: selectedStores.map(x => x.storeNumber).join(','),
      sort: orderBy || defaultSort,
      direction: order || defaultDirection,
      pageNumber: page,
      pageSize: defaultPageSize,
    } as DeliveryScheduleFilterOptions;
  };

  const handleExportODS = async () => {
    setIsLoading(true);
    try {
      const result = await client.exportODS(getFilters(), selectedStore?.storeNumber ?? '');
      if (result && result.success) {
        const blob = base64ToBlob(result.data.fileData, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(blob);
        downloadLink.download = result.data.fileName;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
    } catch (error) {
      handleSnackbarOpen(t('downloadFailed') + ' ' + error, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleODSUpload = async (file: File): Promise<{ success: boolean; data?: ODSImportResultModel; message?: string }> => {
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const result = reader.result as string;
          const base64Data = result.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = error => reject(error);
      });

      const fileRequest = {
        fileData: base64,
        fileName: file.name,
      };

      setCurrentFileRequest(fileRequest);

      const result = await client.validateODS(fileRequest, selectedStore?.storeNumber ?? '', instanceId ?? 0, marketId ?? 0);

      if (result.success) {
        return {
          success: true,
          data: result.data,
          message: t('validationComplete'),
        };
      } else {
        return {
          success: false,
          message: result.message || t('validationFailed'),
        };
      }
    } catch (error) {
      return {
        success: false,
        message: t('uploadFailed') + ': ' + error,
      };
    }
  };

  const handleConfirmODSUpload = async (): Promise<{ success: boolean; message?: string }> => {
    try {
      if (!currentFileRequest) {
        return {
          success: false,
          message: t('fileRequestNotFound'),
        };
      }

      const result = await client.importODS(currentFileRequest, selectedStore?.storeNumber ?? '', instanceId ?? 0, marketId ?? 0, localStorage.getItem('i18nextLng') ?? 'en-GB');

      if (result.success) {
        setCurrentFileRequest(null);

        await loadFilteredData(getFilters());
        handleSnackbarOpen(t('odsChangesAppliedSuccessfully'), 'success');

        return {
          success: true,
          message: t('changesAppliedSuccessfully'),
        };
      } else {
        handleSnackbarOpen(result.message || t('uploadFailed'), 'error');
        return {
          success: false,
          message: result.message || t('uploadFailed'),
        };
      }
    } catch (error) {
      const errorMsg = t('uploadFailed') + ': ' + error;
      handleSnackbarOpen(errorMsg, 'error');
      return {
        success: false,
        message: errorMsg,
      };
    }
  };

  const clearFilters = async () => {
    setSelectedOrderTypes([]);
    setCheckedAllOrderTypes(false);
    setSupplier('');
    setSelectedStores([]);
    setCheckedAllStores(false);
    setDeliveryDateFrom('');
    setDeliveryDateTo('');
    setCutOffDateFrom('');
    setCutOffDateTo('');
    setSelectedItems([]);
    
    setInstanceId(0);
    setMarketId(0);
    setPage(0);
    setPaginatedData(undefined);
  };

  const handleDelete = () => {
    setDeleteConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const result = await client.deleteBulkODS(
        selectedItems,
        selectedStore?.storeNumber ?? '',
      );

      if (result.success) {
        handleSnackbarOpen(t('itemsDeletedSuccessfully'), 'success');
        await loadFilteredData(getFilters());
        setSelectedItems([]);
      } else {
        handleSnackbarOpen(result.message || t('deleteFailed'), 'error');
      }

      setDeleteConfirmModalOpen(false);

    } catch (error) {
      handleSnackbarOpen(t('deleteFailed') + ': ' + error, 'error');
      setDeleteConfirmModalOpen(false);
    }
  };

  const handleSetCutOffDate = () => {
    setDateModalType('cutoff');
    setDateModalOpen(true);
  };

  const handleSetDeliveryDate = () => {
    setDateModalType('delivery');
    setDateModalOpen(true);
  };

  const handleConfirmDateSelection = async (selectedDate: string) => {
    try {
      const request = {
        ids: selectedItems,
        date: selectedDate,
        isCutOffDate: dateModalType == 'cutoff',
        companyId: instanceId ?? 0,
        languageCode: localStorage.getItem('i18nextLng') ?? 'en-GB',
      };

      const result = await client.updateBulkODSDate(request, selectedStore?.storeNumber ?? '');

      if (result.success) {
        const successMessage = dateModalType == 'cutoff'
          ? t('cutOffDateUpdatedSuccessfully')
          : t('deliveryDateUpdatedSuccessfully');
        handleSnackbarOpen(successMessage, 'success');
        await loadFilteredData(getFilters());
        setSelectedItems([]);
      } else {
        handleSnackbarOpen(result.message || t('updateFailed'), 'error');
      }

      setDateModalOpen(false);

    } catch (error) {
      handleSnackbarOpen(t('updateFailed') + ': ' + error, 'error');
    }
  };

  const handleDateModalClose = () => {
    setDateModalOpen(false);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmModalOpen(false);
  };

  const handleCreateODS = async (odsData: AddODSModel) => {
    try {
      const result = await client.addODS(odsData, selectedStore?.storeNumber ?? '');

      if (result.success) {
        handleSnackbarOpen(t('odsCreatedSuccessfully'), 'success');
        await loadFilteredData(getFilters());
      } else {
        handleSnackbarOpen(result.message || t('createODSFailed'), 'error');
        throw new Error(result.message || t('createODSFailed'));
      }

      handleSnackbarOpen(t('odsCreatedSuccessfully'), 'success');
      await loadFilteredData(getFilters());

    } catch (error) {
      logError(error);
      throw error;
    }
  };

  useEffect(() => {
    if (!selectedOrderTypes.map(x => x.orderTypeId).includes(12)) {
      setSupplier('');
    }
  }, [selectedOrderTypes]);

  return (
    <PageArea>
      <Grid container
        display="flex"
        justifyContent="space-between">
        <Grid item
          xs={12}
          md={3}>
          <PageTitle>{t('title')}</PageTitle>
        </Grid>
        <Grid item
          xs={12}
          md={2}>
          <Button
            fullWidth
            disabled={isLoading || !hasWritePermissions}
            startIcon={<AddIcon />}
            size="lg"
            onClick={() => setCreateODSModalOpen(true)}
            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {t('createODS')}
          </Button>
        </Grid>
      </Grid>

      <Grid item
        container
        mt={5}
        xs={12}
        md={12}
      >
        <Grid container>
          <Grid container
            spacing={5}
            flexWrap={'wrap'}
          >
            <Grid item
              xs={12}
              sm={6}
              md={2}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
              <FormControl fullWidth
                required={true}
                size='small'>
                <InputLabel>
                  {t('filterByInstance')}
                </InputLabel>
                <Select
                  value={instanceId === 0 ? '' : instanceId}
                  onChange={(e) => {
                    setPaginatedData(undefined);
                    setInstanceId(Number(e.target.value));
                  }}
                  label={t('filterByInstance')}
                >
                  {instances.map((instance) => (
                    <MenuItem 
                      key={instance.id} 
                      value={instance.id}
                    >
                      {instance.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item
              xs={12}
              sm={6}
              md={2}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
              <FormControl fullWidth
                required={true}
                size='small'>
                <InputLabel>
                  {t('filterByMarket')}
                </InputLabel>
                <Select
                  value={marketId === 0 ? '' : marketId}
                  onChange={(e) => {
                    setPaginatedData(undefined);
                    setMarketId(Number(e.target.value));
                  }}
                  label={t('filterByMarket')}
                  disabled={!instanceId}
                >
                  {markets.map((market) => (
                    <MenuItem
                      key={market.customerTypeId}
                      value={market.customerTypeId.toString()}
                    >
                      {market.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item
              sm={6}
              xs={12}
              md={3}>
              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  multiple
                  limitTags={2}
                  options={orderTypes ?? []}
                  value={selectedOrderTypes}
                  disabled={checkedAllOrderTypes}
                  onChange={(event: SyntheticEvent, value: MarketOrderType[]) => {
                    setSelectedOrderTypes(value);
                  }}
                  getOptionKey={(option) => option.orderTypeId ?? ''}
                  getOptionLabel={(option) => option.name ?? ''}
                  renderInput={(params) => (
                    <TextField {...params}
                      label={t('selectOrderTypes')}
                      placeholder={t('selectOrderTypes')} />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item
              sm={6}
              xs={12}
              md={1}>
              <FormGroup style={{ textAlign: 'right' }}>
                <FormControlLabel
                  control={<Checkbox
                    checked={checkedAllOrderTypes}
                    onChange={handleSelectAllOrderTypesChange} />}
                  label={t('selectAll')}
                />
              </FormGroup>
            </Grid>
            <Grid item
              sm={6}
              xs={12}
              md={2}>
              <Button
                fullWidth
                disabled={isLoading}
                size="lg"
                onClick={clearFilters}
                sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {t('clearFilters')}
              </Button>
            </Grid>
            <Grid item
              sm={6}
              xs={12}
              md={2}>
              <Button
                fullWidth
                variant="secondary"
                disabled={isLoading || instanceId === 0 || marketId === 0}
                startIcon={<DownloadIcon />}
                size="lg"
                onClick={handleExportODS}
                sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                aria-label={t('downloadODS')}
              >
                <span style={{
                  display: 'inline-block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                >
                  {t('downloadODS')}
                </span>
              </Button>
            </Grid>

            <Grid item
              xs={12}
              sm={6}
              md={2}>
              <DatePickerLocalized
                useCalendar={false}
                label={t('deliveryDateFrom')}
                format="DD/MM/YYYY"
                value={deliveryDateFrom}
                translations={{
                  clearButtonLabel: t('calendar.clearButtonLabel'),
                  todayButtonLabel: t('calendar.todayButtonLabel'),
                  cancelButtonLabel: t('calendar.cancelButtonLabel'),
                  okButtonLabel: t('calendar.okButtonLabel'),
                }}
                shouldDisableDate={(date) => {
                  const d = new Date(date);
                  d.setHours(0, 0, 0, 0);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const tomorrow = new Date(today);
                  tomorrow.setDate(today.getDate() + 1);

                  return d < tomorrow;
                }}
                onChange={(newValue) => {
                  if (newValue === '') {
                    setDeliveryDateFrom('');
                  } else if (newValue) {
                    setDeliveryDateFrom(newValue);
                    if (deliveryDateTo && newValue > deliveryDateTo) {
                      setDeliveryDateTo('');
                    }
                  }
                }}
              />
            </Grid>
            <Grid item
              xs={12}
              sm={6}
              md={2}>
              <DatePickerLocalized
                useCalendar={false}
                label={t('deliveryDateTo')}
                format="DD/MM/YYYY"
                translations={{
                  clearButtonLabel: t('calendar.clearButtonLabel'),
                  todayButtonLabel: t('calendar.todayButtonLabel'),
                  cancelButtonLabel: t('calendar.cancelButtonLabel'),
                  okButtonLabel: t('calendar.okButtonLabel'),
                }}
                value={deliveryDateTo}
                onChange={(newValue) => {
                  if (newValue === '') {
                    setDeliveryDateTo('');
                  } else if (newValue) {
                    setDeliveryDateTo(newValue);
                  }
                }}
                shouldDisableDate={(date) => {
                  return deliveryDateFrom ? date < deliveryDateFrom : false;
                }}
              />
            </Grid>

            <Grid item
              sm={6}
              xs={12}
              md={3}>
              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  multiple
                  limitTags={2}
                  options={stores ?? []}
                  value={selectedStores}
                  disabled={checkedAllStores}
                  onChange={(event: SyntheticEvent, value: Store[]) => {
                    setSelectedStores(value);
                  }}
                  getOptionKey={(option) => option.storeNumber ?? ''}
                  getOptionLabel={(option) => option.storeName ?? ''}
                  filterOptions={filterStoreOptions}
                  renderInput={(params) => (
                    <TextField {...params}
                      label={t('selectStores')}
                      placeholder={t('selectStores')} />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item
              sm={6}
              xs={12}
              md={1}>
              <FormGroup style={{ textAlign: 'right' }}>
                <FormControlLabel
                  control={<Checkbox
                    checked={checkedAllStores}
                    onChange={handleSelectAllStoresChange} />}
                  label={t('selectAll')}
                />
              </FormGroup>
            </Grid>

            <Grid item
              lg={2}
              sm={2}
              xs={12}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'right' }}>
              <Button
                variant="primary"
                size="lg"
                disabled={isLoading || instanceId === 0 || marketId === 0}
                startIcon={<SvgIcon> <SearchSm /> </SvgIcon>}
                onClick={searchClick}
                fullWidth>
                {t('search')}
              </Button>
            </Grid>

            <Grid item
              xs={12}
              md={2}>
              <Button
                fullWidth
                variant="secondary"
                disabled={isLoading || instanceId === 0 || marketId === 0 || !hasWritePermissions}
                startIcon={<UploadIcon />}
                size="lg"
                onClick={() => setUploadModalOpen(true)}
                sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {t('uploadODS')}
              </Button>
            </Grid>

            <Grid item
              xs={12}
              sm={6}
              md={2}>
              <DatePickerLocalized
                useCalendar={false}
                label={t('cutOffDateFrom')}
                format="DD/MM/YYYY"
                value={cutOffDateFrom}
                translations={{
                  clearButtonLabel: t('calendar.clearButtonLabel'),
                  todayButtonLabel: t('calendar.todayButtonLabel'),
                  cancelButtonLabel: t('calendar.cancelButtonLabel'),
                  okButtonLabel: t('calendar.okButtonLabel'),
                }}
                shouldDisableDate={(date) => {
                  const d = new Date(date);
                  d.setHours(0, 0, 0, 0);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const tomorrow = new Date(today);
                  tomorrow.setDate(today.getDate() + 1);

                  return  d < tomorrow;
                }}
                onChange={(newValue) => {
                  if (newValue === '') {
                    setCutOffDateFrom('');
                  } else if (newValue) {
                    setCutOffDateFrom(newValue);
                    if (cutOffDateTo && newValue > cutOffDateTo) {
                      setCutOffDateTo('');
                    }
                  }
                }}
              />
            </Grid>
            <Grid item
              xs={12}
              sm={6}
              md={2}>
              <DatePickerLocalized
                useCalendar={false}
                label={t('cutOffDateTo')}
                format="DD/MM/YYYY"
                translations={{
                  clearButtonLabel: t('calendar.clearButtonLabel'),
                  todayButtonLabel: t('calendar.todayButtonLabel'),
                  cancelButtonLabel: t('calendar.cancelButtonLabel'),
                  okButtonLabel: t('calendar.okButtonLabel'),
                }}
                value={cutOffDateTo}
                onChange={(newValue) => {
                  if (newValue === '') {
                    setCutOffDateTo('');
                  } else if (newValue) {
                    setCutOffDateTo(newValue);
                  }
                }}
                shouldDisableDate={(date) => {
                  return cutOffDateFrom ? date < cutOffDateFrom : false;
                }}
              />
            </Grid>
            
            <Grid item
              xs={12}
              sm={6}
              md={2}
              sx={{ display: selectedOrderTypes.map(x => x.orderTypeId).includes(12) ? 'flex' : 'none', flexDirection: 'column', alignItems: 'left' }}>
              <FormControl fullWidth>
                <InputLabel shrink>
                  {t('supplier')}
                </InputLabel>
                <Select
                  value={supplier}
                  onChange={(e) => {
                    setSupplier(e.target.value);
                  }}
                  label={t('supplier')}
                  displayEmpty
                  notched
                >
                  <MenuItem key='all'
                    value=''>
                    {t('all')}
                  </MenuItem>
                  {suppliers.map((supplier) => (
                    <MenuItem
                      key={supplier.supplierNumber}
                      value={supplier.supplierNumber.toString()}
                    >
                      {supplier.supplierName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

          </Grid>
          {
            isLoading && paginatedData ? <Grid item
              xs={12}
              sx={{ mt: '12px' }}>
              <LoadingWheel />
            </Grid> :
              (!isLoading && paginatedData && <Grid item
                xs={12}
                sx={{ mt: '12px' }}>
                <Section>
                  <DeliveryScheduleTable 
                    list={paginatedData}
                    page={page}
                    setPage={setPage} 
                    order={order} 
                    orderBy={orderBy}
                    setOrder={setOrder} 
                    setOrderBy={setOrderBy} 
                    isLoading={isLoading} 
                    hasWritePermissions={hasWritePermissions}     
                    hasDeletePermissions={hasDeletePermissions}          
                    selectedItems={selectedItems}
                    onItemSelectionChange={handleItemSelectionChange}
                    onSetCutOffDate={handleSetCutOffDate} 
                    onSetDeliveryDate={handleSetDeliveryDate}
                    onDelete={handleDelete}
                    isSelectAll={isSelectAll}
                    onSelectAllChange={handleSelectAllChange}
                  />
                </Section>
              </Grid>)
          }
        </Grid>
      </Grid >
      <MessagesSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
        duration={snackbarSeverity == 'error' ? 15000 : 5000}
      />

      <ODSUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onFileSelected={handleODSUpload}
        onConfirmUpload={handleConfirmODSUpload}
        title={t('uploadODSFile')}
        uploadLabel={t('dragDropODSFileHere')}
        uploadSublabel={t('acceptsExcelFiles')}
      />

      <DateSelectionModal
        open={dateModalOpen}
        onClose={handleDateModalClose}
        onConfirm={handleConfirmDateSelection}
        selectedItemsCount={selectedItems.length}
        dateType={dateModalType}
      />

      <ConfirmationDialog
        open={deleteConfirmModalOpen}
        message={t('deleteModalMessage')}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        icon={<Trash01 />}
        title={t('deleteConfirmText')}
        variant='bad'
        confirmButtonText={t('confirm')}
        cancelButtonText={t('cancel')}
      />

      <AddODSModal
        open={createODSModalOpen}
        onClose={() => setCreateODSModalOpen(false)}
        onConfirm={handleCreateODS}
        title={t('createNewODS')}
        instances={instances}
      />
    </PageArea >

  );
};

export default ManageDeliverySchedule; 